import {
  LA_MODULES,
  LA_EXPANSION_MODULES,
  getLaModuleParts,
  getLaModulePath,
} from "./laModules";
import { PS_EXPANSION_MODULES, getPsModulePath } from "./psModules";
/** Top-level subject paths shown on Home ("Choose a path"). */
export const COURSES = [
  {
    id: "calculus-analytical-geometry",
    title: "Calculus and Analytical Geometry",
    logo: (
      <span className="math-logo-card">
        <span className="math-sym-big">∫</span> f(x) dx
      </span>
    ),
    description:
      "Single-variable foundations: limits, differentiation, integration, sequences & series, conics, and Taylor series — certificate-ready theory and examples.",
    path: "/courses/calculus-analytical-geometry",
    meta: "6 guides · Tools + practice",
    icon: "∫",
    color: "gold",
    heroImage: "/images/courses/calc-geometry-hero.svg",
    heroImageAlt: "Calculus and Analytical Geometry Curves and Tangents Visual",
    overview: {
      longDescription: [
        "Calculus and Analytical Geometry forms the mathematical bedrock for modern science, engineering, and computational modeling. This rigorous curriculum establishes first principles of limits and continuity via epsilon-delta formulations, the Squeeze Theorem, and intermediate value properties. We develop differential calculus from difference quotients through product, quotient, and chain rules, analyzing curve optimization, related rates, and Mean Value Theorems. Integral calculus bridges accumulation via Riemann sums to the Fundamental Theorem of Calculus, improper integrals, and geometric volume applications. The course advances through infinite series convergence tests, Taylor and Maclaurin expansions, and concludes with Cartesian coordinate geometry and complete conic section classifications.",
      ],
      highlights: [
        "Limits, epsilon-delta proofs & squeeze theorem",
        "Differentiation rules, extrema, MVT & L'Hôpital",
        "FTC, area, integration techniques & improper integrals",
        "Convergence tests, power series & Taylor expansions",
        "Parabolas, ellipses, hyperbolas & analytic geometry",
        "TaylorX interactive derivative visualizer",
        "Certificate quiz — 120 MCQs, 80% to pass",
      ],
      prerequisites: "Algebra, basic trigonometry, and function notation.",
    },
    modules: [
      {
        title: "Overview",
        description:
          "What you'll learn, the order to learn it in, and how the course and certificate are structured.",
        path: "/calculus/overview",
        meta: "Start here",
        icon: "★",
        logo: <span className="math-logo">∫ f(x) dx Roadmap</span>,
        start: true,
      },
      {
        title: "Limits & Continuity",
        description:
          "Limits, path tests, continuity — with certificate-depth theory and worked examples.",
        path: "/limits-continuity/1",
        meta: "2 parts · 6+ examples each",
        icon: "lim",
        logo: (
          <span className="math-logo">
            lim<sub>x→a</sub> f(x)
          </span>
        ),
      },
      {
        title: "Differentiation",
        description:
          "Definition, rules, related rates, extrema, MVT, L'Hôpital — certificate track.",
        path: "/differentiation/1",
        meta: "2 parts · 6 examples each",
        icon: "d/dx",
        logo: (
          <span className="math-logo">
            f'(x) = <sup>df</sup>/<sub>dx</sub>
          </span>
        ),
      },
      {
        title: "Integration",
        description:
          "Antiderivatives, FTC, area, techniques, improper integrals — certificate track.",
        path: "/integration/1",
        meta: "2 parts · 6 examples each",
        icon: "∫",
        logo: <span className="math-logo">∫ f(x) dx</span>,
      },
      {
        title: "Sequences & Infinite Series",
        description:
          "Convergence tests, power series, and radius of convergence — certificate track.",
        path: "/sequences-series/1",
        meta: "2 parts · 8 examples each",
        icon: "Σ",
        logo: (
          <span className="math-logo">
            Σ<sub>n=1</sub>
            <sup>∞</sup> a<sub>n</sub>
          </span>
        ),
      },
      {
        title: "Conic Sections",
        description:
          "Parabolas, ellipses, hyperbolas, and analytic geometry — certificate track.",
        path: "/conic-sections/1",
        meta: "2 parts · 8 examples each",
        icon: "◯",
        logo: <span className="math-logo">x²/a² + y²/b² = 1</span>,
      },
      {
        title: "Straight Lines & Systems of Lines",
        description:
          "Slope, intercepts, normal form, angle between lines, concurrency, and homogeneous pairs of lines (F.Sc Ch. 4 + Math-101).",
        path: "/lines-geometry/1",
        meta: "2 parts · 16 lengthy examples · 30 MCQs",
        icon: "📏",
        logo: (
          <span className="math-logo">
            Ax + By + C = 0 &amp; ax² + 2hxy + by² = 0
          </span>
        ),
      },
      {
        title: "Circle & Conic Tangents/Normals",
        description:
          "Standard/general circles, 3-point circles, parametric forms, T = 0 operator, tangency conditions, and director circles (F.Sc Ch. 6 + Math-101).",
        path: "/circles-tangents/1",
        meta: "2 parts · 16 lengthy examples · 30 MCQs",
        icon: "⭕",
        logo: (
          <span className="math-logo">
            x² + y² + 2gx + 2fy + c = 0 &amp; T = 0
          </span>
        ),
      },
      {
        title: "Advanced Single-Variable Calculus",
        description:
          "Epsilon-delta limit proofs, discontinuity classification, IVT/EVT/MVT, logarithmic/parametric differentiation, hyperbolics, and curvature (F.Sc Ch. 1-3 + Math-101).",
        path: "/advanced-calculus/1",
        meta: "2 parts · 16 lengthy examples · 30 MCQs",
        icon: "∂",
        logo: (
          <span className="math-logo">
            (ε, δ) &amp; κ = |y''| / (1+y'²)^(3/2)
          </span>
        ),
      },
      {
        title: "Ordinary Differential Equations",
        description:
          "Separable, homogeneous, integrating factor, exact, Bernoulli, and 2nd-order constant coefficient ODEs (F.Sc Ch. 3 + Math-201).",
        path: "/differential-equations/1",
        meta: "2 parts · 16 lengthy examples · 30 MCQs",
        icon: "⚡",
        logo: (
          <span className="math-logo">
            y' + P(x)y = Q(x) &amp; ay'' + by' + cy = 0
          </span>
        ),
      },
      {
        title: "Space Curves (Frenet-Serret)",
        description:
          "Arc length parameterization, TNB moving trihedron, curvature, torsion, and osculating geometry.",
        path: "/space-curves/1",
        meta: "2 parts · 20 MCQs",
        icon: "🌀",
        logo: <span className="math-logo">T, N, B &amp; κ, τ</span>,
      },
      {
        title: "Vector-Valued Functions & Motion",
        description:
          "Kinematics in ℝ³, velocity, speed, tangential/normal acceleration components, and central orbital forces.",
        path: "/vector-motion/1",
        meta: "2 parts · 20 MCQs",
        icon: "🚀",
        logo: <span className="math-logo">r'(t), a_T T + a_N N</span>,
      },
      {
        title: "Parametric Surfaces",
        description:
          "Surface coordinate grids, tangent planes, normal fields, differential surface area, and ruled minimal manifolds.",
        path: "/parametric-surfaces/1",
        meta: "2 parts · 20 MCQs",
        icon: "🌐",
        logo: <span className="math-logo">r_u × r_v &amp; dS</span>,
      },
      {
        title: "Polar Coordinate Calculus",
        description:
          "Polar tangents, area bounded by cardioids and rose petals, polar arc length, and curvature.",
        path: "/polar-calculus/1",
        meta: "2 parts · 20 MCQs",
        icon: "🎯",
        logo: <span className="math-logo">r = f(θ) &amp; ½∫r²dθ</span>,
      },
      {
        title: "Solids of Revolution",
        description:
          "Disk, washer, and cylindrical shells methods, rotations about arbitrary axes, and Pappus's Centroid Theorem.",
        path: "/solids-revolution/1",
        meta: "2 parts · 20 MCQs",
        icon: "🏺",
        logo: <span className="math-logo">π∫R²dx &amp; 2π∫r h dx</span>,
      },
      {
        title: "Volume by Cross-Sections",
        description:
          "Non-revolution volume slicing with square, triangular, and semicircular profiles, wedges, and bicylinders.",
        path: "/volume-cross-sections/1",
        meta: "2 parts · 20 MCQs",
        icon: "📦",
        logo: <span className="math-logo">V = ∫ A(x) dx</span>,
      },
      {
        title: "Numerical Methods",
        description:
          "Newton-Raphson quadratic root convergence, Trapezoidal Rule, Simpson's 1/3 Rule, and Romberg extrapolation.",
        path: "/numerical-methods/1",
        meta: "2 parts · 20 MCQs",
        icon: "🧮",
        logo: <span className="math-logo">xₙ₊₁ = xₙ − f/f' &amp; Sₙ</span>,
      },
      {
        title: "Improper Integrals — Advanced Tests",
        description:
          "Infinite horizons, singular asymptotes, Direct/Limit comparison, Cauchy Principal Value, and Gamma/Beta functions.",
        path: "/improper-integrals-advanced/1",
        meta: "2 parts · 20 MCQs",
        icon: "♾️",
        logo: <span className="math-logo">P.V. ∫ &amp; Γ(z), B(p,q)</span>,
      },
      {
        title: "Complex Numbers & De Moivre",
        description:
          "Polar Euler representation, De Moivre's theorem, n-th roots of unity, and multiple-angle trigonometric expansions.",
        path: "/complex-numbers/1",
        meta: "2 parts · 20 MCQs",
        icon: "ℂ",
        logo: <span className="math-logo">e^(iθ) &amp; (cosθ+isinθ)ⁿ</span>,
      },
      {
        title: "Hyperbolic Functions & Inverses",
        description:
          "Hyperbolic identities, logarithmic inverse expressions, standard radical integrals, and catenary cable geometry.",
        path: "/hyperbolic-functions/1",
        meta: "2 parts · 20 MCQs",
        icon: "〰️",
        logo: <span className="math-logo">cosh²x - sinh²x = 1</span>,
      },
      {
        title: "Laplace Transforms",
        description:
          "Exponential kernel mappings, frequency/time shifting, derivative transforms, convolutions, and ODE solutions.",
        path: "/laplace-transforms/1",
        meta: "2 parts · 20 MCQs",
        icon: "ℒ",
        logo: (
          <span className="math-logo">ℒ&#123;f(t)&#125; = ∫e^(-st)f(t)dt</span>
        ),
      },
      {
        title: "Fourier Series",
        description:
          "Periodic function decomposition, Euler-Fourier coefficients, half-range cosine/sine series, and Parseval's identity.",
        path: "/fourier-series/1",
        meta: "2 parts · 20 MCQs",
        icon: "📈",
        logo: <span className="math-logo">½a₀ + ∑(aₙcos + bₙsin)</span>,
      },
      {
        title: "Gamma & Beta Functions",
        description:
          "Euler's first and second integrals, factorial continuation, reflection and duplication identities, and Stirling asymptotics.",
        path: "/gamma-beta-functions/1",
        meta: "2 parts · 20 MCQs",
        icon: "Γ",
        logo: <span className="math-logo">Γ(z) = ∫₀^∞ t^(z-1)e^(-t)dt</span>,
      },
      {
        title: "Differentiation Under Integral Sign",
        description:
          "Leibniz integral rule with fixed and variable limits, dominated convergence, and Feynman's parametric evaluation trick.",
        path: "/leibniz-rule/1",
        meta: "2 parts · 20 MCQs",
        icon: "∂",
        logo: <span className="math-logo">d/dx ∫ f(x,t)dt</span>,
      },
      {
        title: "Fourier Transform & Convolution",
        description:
          "Continuous frequency spectrum, inverse Fourier transform, continuous convolution theorem, and Plancherel energy conservation.",
        path: "/fourier-transforms/1",
        meta: "2 parts · 20 MCQs",
        icon: "📡",
        logo: <span className="math-logo">F(ω) = ∫ f(t)e^(-iωt)dt</span>,
      },
      {
        title: "Z-Transform & Difference Equations",
        description:
          "Discrete Laurent series, ROC convergence topology, difference equations, inverse Z-transforms, and filter stability.",
        path: "/z-transforms/1",
        meta: "2 parts · 20 MCQs",
        icon: "🔲",
        logo: <span className="math-logo">X(z) = ∑ x[n]z^(-n)</span>,
      },
      {
        title: "Introduction to PDEs (Heat & Wave)",
        description:
          "Separation of variables for 1D heat diffusion and 1D acoustic waves, spatial eigenvalues, and Fourier modal expansions.",
        path: "/intro-pdes/1",
        meta: "2 parts · 20 MCQs",
        icon: "🌊",
        logo: (
          <span className="math-logo">u_t = α²u_xx &amp; u_tt = c²u_xx</span>
        ),
      },
      {
        title: "Orthogonal Polynomials",
        description:
          "Legendre, Chebyshev, and Hermite families, Rodrigues differential formulas, 3-term recurrences, and Chebyshev nodes.",
        path: "/orthogonal-polynomials/1",
        meta: "2 parts · 20 MCQs",
        icon: "🏛️",
        logo: <span className="math-logo">Pₙ(x), Tₙ(x), Hₙ(x)</span>,
      },
      {
        title: "Pointwise & Uniform Convergence",
        description:
          "Pointwise vs uniform convergence of function sequences, supremum-norm bounds, uniform-limit continuity, and Dirichlet/Weierstrass tests.",
        path: "/pointwise-uniform-convergence/1",
        meta: "2 parts · 20 MCQs",
        icon: "📏",
        logo: <span className="math-logo">||fₙ - f||_∞ → 0</span>,
      },
      {
        title: "Interchanging Limits, Derivatives & Integrals",
        description:
          "Sufficient conditions for termwise integration and differentiation, classic failure cases, and bounded convergence foundations.",
        path: "/interchanging-limits/1",
        meta: "2 parts · 20 MCQs",
        icon: "🔄",
        logo: <span className="math-logo">lim ∫ = ∫ lim</span>,
      },
      {
        title: "Power-Series Solutions of ODEs",
        description:
          "Analytic ODE solutions at ordinary points, recurrence relations, fundamental solution bases, and convergence radii.",
        path: "/power-series-ode/1",
        meta: "2 parts · 20 MCQs",
        icon: "📜",
        logo: <span className="math-logo">y = ∑ aₙxⁿ</span>,
      },
      {
        title: "Frobenius Method (Singular ODEs)",
        description:
          "Regular singular points, indicial equations, distinct roots, repeated roots, and integer difference logarithmic solutions.",
        path: "/frobenius-method/1",
        meta: "2 parts · 20 MCQs",
        icon: "🔍",
        logo: <span className="math-logo">xʳ ∑ aₙxⁿ &amp; F(r)=0</span>,
      },
      {
        title: "Analytic Functions & Cauchy-Riemann",
        description:
          "Complex differentiability, Cartesian and polar Cauchy-Riemann equations, harmonic functions, conjugates, and Milne-Thomson method.",
        path: "/analytic-functions/1",
        meta: "2 parts · 20 MCQs",
        icon: "⚡",
        logo: <span className="math-logo">u_x=v_y, u_y=-v_x</span>,
      },
      {
        title: "Contour Integrals & Residues",
        description:
          "Cauchy-Goursat theorem, Cauchy integral formulas, Laurent series singularity classification, and real integral evaluations.",
        path: "/contour-integrals/1",
        meta: "2 parts · 20 MCQs",
        icon: "∮",
        logo: <span className="math-logo">∮ f(z)dz = 2πi ∑Res</span>,
      },
      {
        title: "Analytic & 3D Vector Lab",
        description:
          "Interactive laboratory for 2D conic rotation of axes, polar conics, scalar triple product, and skew line distance.",
        path: "/analytic-vector-lab",
        meta: "Interactive Suite",
        icon: "📐",
        logo: <span className="math-logo">B²-4AC &amp; u·(v×w)</span>,
      },

      {
        title: "Continuity Finder",
        description:
          "Interactive tool for checking continuity conditions at a point or over an interval.",
        path: "/test",
        meta: "Interactive tool",
        icon: "lim",
        logo: (
          <span className="math-logo">
            lim<sub>x→c</sub> f(x) = f(c)
          </span>
        ),
      },
      {
        title: "Taylor Series",
        description:
          "Local approximations, Maclaurin catalogs, convergence and error — certificate depth.",
        path: "/taylor-series/1",
        meta: "2 parts · 6+ examples each",
        icon: "Σ",
        logo: (
          <span className="math-logo">
            Σ{" "}
            <sup>
              f<sup>(n)</sup>(a)
            </sup>
            /<sub>n!</sub> (x-a)<sup>n</sup>
          </span>
        ),
      },
      {
        title: "TaylorX (Derivative Visualizer)",
        description:
          "Interactive derivative visualizer that pairs with the Taylor Series guide.",
        path: "/taylorx",
        meta: "Interactive tool",
        icon: "Σ",
        logo: (
          <span className="math-logo">
            T<sub>n</sub>(x) ≈ f(x)
          </span>
        ),
      },
      {
        title: "Function Grapher",
        description:
          "Plot functions, trace values, and see derivatives, tangents, roots and turning points.",
        path: "/grapher",
        meta: "Interactive · tool",
        icon: "📈",
        logo: <span className="math-logo">y = f(x)</span>,
      },
      {
        title: "Timed Mock Exam",
        description:
          "30 mixed practice MCQs (10 Easy · 10 Medium · 10 Hard) in 45 minutes, with a topic-by-topic breakdown.",
        path: "/mock-exam/calculus-analytical-geometry",
        meta: "Interactive · 30 questions · 45 min",
        icon: "⏱",
        logo: <span className="math-logo">⏱ 45:00</span>,
      },
      {
        title: "Certification Quiz",
        description:
          "90 MCQs covering the full course. Score 80%+ to unlock your certificate.",
        path: "/quiz/calculus-analytical-geometry",
        meta: "90 questions · 80% to pass",
        icon: "✓",
        logo: <span className="math-logo">✓ 90 MCQs · 80%</span>,
      },
    ],
  },
  {
    id: "multivariable-calculus",
    title: "Multivariable Calculus",
    logo: (
      <span className="math-logo-card">
        <span className="math-sym-big">∭</span> curl{" "}
        <span className="math-bold">F</span>
      </span>
    ),
    description:
      "Partial derivatives, vector calculus, multiple integrals, Lagrange multipliers, divergence & curl, and Stokes.",
    path: "/courses/multivariable-calculus",
    meta: "Full study path · Guides + tools",
    icon: "∇",
    color: "teal",
    heroImage: "/images/courses/multivariable-calc-hero.svg",
    heroImageAlt: "Multivariable Calculus 3D Surface and Gradient Field",
    overview: {
      longDescription: [
        "Multivariable Calculus extends single-variable analysis to multidimensional coordinate spaces and physical vector field theory. The curriculum begins with functions of several variables, level surfaces, and partial derivatives, formalizing tangent planes, total differentials, gradient vectors, and directional derivatives. We analyze unconstrained multivariable optimization alongside Lagrange Multipliers for constrained systems. The course develops multiple integration across Cartesian, polar, cylindrical, and spherical coordinates using Fubini's Theorem and Jacobian transformations. Vector calculus then formalizes line and surface integrals, conservative work fields, and the cornerstone integral theorems—Green's, Gauss's Divergence, and Stokes' Theorems—connecting differential field divergence and curl to macro-scale boundary fluxes across science.",
        "Additional-depth modules extend the core path: 3D analytical geometry and quadric surfaces; space curves with Frenet–Serret frames, curvature and torsion; coordinate transformations, curvilinear systems and flux through parameterized surfaces; the Hessian, KKT conditions, global extrema and gradient descent; and vector potentials, multivariable Taylor series, the Implicit Function Theorem and n-dimensional directional derivatives. The course closes with potential theory (the Laplacian, harmonic functions, Maxwell's equations and the continuity equation), differential forms with the exterior derivative and the generalized Stokes' theorem, and advanced integration—improper multiple integrals, the Gaussian integral and the calculus of variations via the Euler–Lagrange equation. The geometry of surfaces then measures lengths, angles and areas on curved surfaces through the first fundamental form, and describes how they bend through curvature and geodesics. Integration on moving domains slices regions along level sets with the coarea formula and differentiates integrals over moving regions with the Reynolds transport theorem. Nonlinear dynamical systems close the path with phase portraits, Jacobian linearization and Lyapunov stability methods.",
      ],
      highlights: [
        "Partial derivatives, gradients & tangent planes",
        "Double & triple integrals in multiple coordinate systems",
        "Lagrange multipliers & constrained optimization",
        "Line integrals, conservative fields & Green's theorem",
        "Divergence, curl & the Divergence Theorem",
        "Stokes' Theorem & surface integrals",
        "3D analytical geometry & quadric surfaces",
        "Space curves, Frenet–Serret frames, curvature & torsion",
        "Jacobians, curvilinear coordinates & flux integrals",
        "Hessian, KKT conditions & gradient descent",
        "Vector potentials, Taylor series & the Implicit Function Theorem",
        "Laplacian, harmonic functions & Maxwell's equations",
        "Differential forms & the generalized Stokes' theorem",
        "Gaussian integral & the Euler–Lagrange equation",
        "Surface metrics, the first fundamental form, surface curvature & geodesics",
        "Coarea formula, level-set integration & the Reynolds transport theorem",
        "Phase planes, Jacobian linearization & Lyapunov stability",
        "Extreme-value finder, volume calculator & vector-field visualizer",
        "Certificate quiz — 100 MCQs, 80% to pass",
      ],
      prerequisites:
        "Single-variable calculus (limits, derivatives, integrals) and basic linear algebra.",
    },
    modules: [
      {
        title: "Overview",
        description:
          "What you'll learn, the order to learn it in, and how the course and certificate are structured.",
        path: "/multivariable-calculus/overview",
        meta: "Start here",
        icon: "★",
        logo: <span className="math-logo">∇ &amp; ∭ Roadmap</span>,
        start: true,
      },
      {
        title: "Partial Derivatives",
        description:
          "Limits, gradients, tangent planes, differentials, and optimization.",
        path: "/partial-derivatives/1",
        meta: "2 parts · MCQ practice",
        icon: "∂",
        logo: <span className="math-logo">∂f/∂x, ∂f/∂y</span>,
      },
      {
        title: "3D Surface Explorer",
        description:
          "Rotate, animate and probe surfaces z = f(x, y) with tangent planes.",
        path: "/surface-explorer",
        meta: "Interactive tool",
        icon: "⛰",
        logo: <span className="math-logo">z = f(x, y)</span>,
      },
      {
        title: "Extreme Value Finder",
        description: "Find maxima and minima using the second derivative test.",
        path: "/extreme",
        meta: "Interactive tool",
        icon: "⬆",
        logo: (
          <span className="math-logo">
            D = f<sub>xx</sub>f<sub>yy</sub> - f<sub>xy</sub>²
          </span>
        ),
      },
      {
        title: "Vector Calculus",
        description:
          "Line integrals, conservative fields, Green's theorem, and surfaces.",
        path: "/vector-calculus/1",
        meta: "2 parts · Worked examples",
        icon: "∇",
        logo: (
          <span className="math-logo">
            ∮<sub>C</sub> F · dr
          </span>
        ),
      },
      {
        title: "Vector Field Visualizer",
        description: "Plot and explore vector fields interactively.",
        path: "/vectorfield",
        meta: "Interactive tool",
        icon: "∇",
        logo: <span className="math-logo">F(x,y) = ⟨P, Q⟩</span>,
      },
      {
        title: "Multiple Integrals",
        description:
          "Double and triple integrals, Fubini, polar and cylindrical coordinates.",
        path: "/multiple-integrals/1",
        meta: "2 parts · Worked integrals",
        icon: "∬",
        logo: (
          <span className="math-logo">
            ∬<sub>R</sub> f(x,y) dA
          </span>
        ),
      },
      {
        title: "Volume Calculator",
        description: "Evaluate double integrals with full step-by-step output.",
        path: "/volumecalculator",
        meta: "Interactive tool",
        icon: "∬",
        logo: <span className="math-logo">V = ∬ (z₂ - z₁) dA</span>,
      },
      {
        title: "Lagrange Multipliers",
        description: "Constrained optimization via gradient alignment.",
        path: "/lagrange-multipliers/1",
        meta: "2 parts · Applications",
        icon: "λ",
        logo: <span className="math-logo">∇f = λ ∇g</span>,
      },
      {
        title: "Divergence & Curl",
        description:
          "Vector field operators, identities, divergence theorem, and Stokes connections.",
        path: "/divergence-curl/1",
        meta: "2 parts · Theorems",
        icon: "∇·",
        logo: <span className="math-logo">div F, curl F</span>,
      },
      {
        title: "Stokes' Theorem",
        description: "Circulation, oriented surfaces, and Stokes applications.",
        path: "/stokes-theorem/1",
        meta: "2 parts · Theorem applications",
        icon: "∮",
        logo: (
          <span className="math-logo">
            ∮<sub>∂S</sub> F·dr = ∬<sub>S</sub> (∇×F)·dS
          </span>
        ),
      },
      {
        title: "3D Analytical Geometry & Quadric Surfaces",
        description:
          "Direction cosines, planes and lines in 3D, skew lines, and the six standard quadric surfaces.",
        path: "/3d-geometry/1",
        meta: "2 parts · MCQ practice",
        icon: "⬡",
        logo: <span className="math-logo">x²/a² + y²/b² − z²/c² = 1</span>,
      },
      {
        title: "Space Curves & Advanced Multivariable Mappings",
        description:
          "Vector-valued functions, velocity and acceleration, Frenet–Serret frames, curvature, torsion, Jacobians, surface and flux integrals, and global extrema.",
        path: "/space-curves/1",
        meta: "2 parts · 140 MCQ practice",
        icon: "∿",
        logo: <span className="math-logo">∇f · dS</span>,
      },
      {
        title: "Coordinate Transformations & Surfaces",
        description:
          "Jacobians & Change of Variables, Curvilinear Coordinate Systems, Parametrized Surface Area, and Flux Integrals over General Parameterized Surfaces.",
        path: "/coordinate-transformations/1",
        meta: "2 parts · 4 topics · 20-question checkpoint per topic",
        icon: "J",
        logo: <span className="math-logo">∂(x,y) / ∂(u,v)</span>,
      },
      {
        title: "Constrained & Unconstrained Optimization",
        description:
          "The Hessian Matrix & Optimization, Inequality Constraints (KKT Conditions), Global Extrema on Bounded Domains, and Gradient Descent & Numerical Optimization.",
        path: "/constrained-unconstrained-optimization/1",
        meta: "4 topics · 20-question checkpoint per topic",
        icon: "H",
        logo: <span className="math-logo">H = ∇²f</span>,
      },
      {
        title: "Vector Fields & Approximation Theory",
        description:
          "Vector Potentials, Multivariable Taylor Series & Second-Order Approximation, Implicit Function Theorem, and Directional Derivatives in n Dimensions.",
        path: "/vector-fields-approximation/1",
        meta: "4 topics · 20-question checkpoint per topic",
        icon: "∇",
        logo: <span className="math-logo">∇×A = F</span>,
      },
      {
        title: "Potential Theory",
        description:
          "The Laplacian & harmonic functions, then Maxwell's equations & the continuity equation.",
        path: "/multivariable-calculus/potential-theory",
        meta: "Laplacian & Maxwell · 20 MCQs each",
        icon: "Δ",
        logo: <span className="math-logo">∇²φ = 0</span>,
      },
      {
        title: "Differential Forms",
        description:
          "Differential forms & the exterior derivative, then the Generalized Stokes' Theorem.",
        path: "/multivariable-calculus/differential-forms",
        meta: "Forms & Generalized Stokes · 20 MCQs each",
        icon: "∧",
        logo: <span className="math-logo">d² = 0</span>,
      },
      {
        title: "Advanced Integration & Variational Methods",
        description:
          "Improper multiple integrals & the Gaussian integral, then the calculus of variations and the Euler–Lagrange equation.",
        path: "/multivariable-calculus/advanced-integration",
        meta: "Improper Integrals & Variations · 20 MCQs each",
        icon: "∫",
        logo: <span className="math-logo">∫ e^(−x²) dx = √π</span>,
      },
      {
        title: "Geometry of Surfaces",
        description:
          "Surface Metrics and the First Fundamental Form, then Surface Curvature and Geodesics.",
        path: "/multivariable-calculus/surface-geometry",
        meta: "Metrics & Curvature · 20 MCQs each",
        icon: "𝐈",
        logo: <span className="math-logo">ds² = E du² + 2F du dv + G dv²</span>,
      },
      {
        title: "Integration on Moving Domains",
        description:
          "Coarea Formula and Level-Set Integration, then the Reynolds Transport Theorem.",
        path: "/multivariable-calculus/moving-domains",
        meta: "Coarea & Reynolds · 20 MCQs each",
        icon: "∮",
        logo: <span className="math-logo">∬ f|∇u| dA = ∫∫ f ds dt</span>,
      },
      {
        title: "Nonlinear Dynamical Systems",
        description:
          "Phase Planes and Local Stability, then Lyapunov Stability Methods.",
        path: "/multivariable-calculus/nonlinear-dynamics",
        meta: "Phase Planes & Lyapunov · 20 MCQs each",
        icon: "⟳",
        logo: <span className="math-logo">x′ = F(x), V̇ = ∇V·F</span>,
      },
      {
        title: "Function Grapher",
        description:
          "Plot functions, trace values, and see derivatives, tangents, roots and turning points.",
        path: "/grapher",
        meta: "Interactive · tool",
        icon: "📈",
        logo: <span className="math-logo">y = f(x)</span>,
      },
      {
        title: "Timed Mock Exam",
        description:
          "30 mixed practice MCQs (10 Easy · 10 Medium · 10 Hard) in 45 minutes, with a topic-by-topic breakdown.",
        path: "/mock-exam/multivariable-calculus",
        meta: "Interactive · 30 questions · 45 min",
        icon: "⏱",
        logo: <span className="math-logo">⏱ 45:00</span>,
      },
      {
        title: "Certification Quiz",
        description:
          "100 MCQs covering the full course. Score 80%+ to unlock your certificate.",
        path: "/quiz/multivariable-calculus",
        meta: "100 questions · 80% to pass",
        icon: "✓",
        logo: <span className="math-logo">✓ 100 MCQs · 80%</span>,
      },
    ],
  },
  {
    id: "linear-algebra",
    title: "Linear Algebra",
    logo: (
      <span className="math-logo-card">
        <span className="math-sym-big">A</span>x = b
      </span>
    ),
    description:
      "Vectors, matrices, linear systems, eigenvalues, and the algebraic toolkit used across calculus and data science.",
    path: "/courses/linear-algebra",
    meta: "11 modules · Guides + tools",
    icon: "A",
    color: "blue",
    heroImage: "/images/courses/linear-algebra-hero.svg",
    heroImageAlt: "Linear Algebra Vectors and Matrix Transformation Visual",
    overview: {
      longDescription: [
        "Linear Algebra provides the universal mathematical framework for modern computation, machine learning, data science, and quantum physics. We establish vector space algebra in n-dimensional Euclidean space, investigating linear combinations, span, basis sets, dimensions, and fundamental subspaces. Matrix operations, Gaussian elimination, row reduction, rank-nullity invariants, and determinant volumes characterize linear system solvability and matrix invertibility. The curriculum explores linear transformations, spectral eigenvalue decompositions, and the Spectral Theorem for symmetric operators. Advanced modules formalize inner product orthogonality, Gram-Schmidt QR factorizations, and least-squares normal equations, culminating in the Singular Value Decomposition (SVD) and Principal Component Analysis for optimal high-dimensional data reduction and modeling.",
      ],
      highlights: [
        "Vectors, span, basis & linear independence",
        "Matrix operations, inverses & determinants",
        "Row reduction, rank & solution-set geometry",
        "Eigenvalues, eigenvectors & diagonalization",
        "Linear transformations, kernel & image",
        "Gram–Schmidt, QR & least-squares fitting",
        "SVD, low-rank approximation & the pseudoinverse",
        "Interactive Matrix Sandbox",
      ],
      prerequisites:
        "Comfortable with algebra and systems of equations; no prior linear algebra needed.",
    },
    modules: [
      {
        title: "Overview",
        description:
          "What you'll learn, the order to learn it in, and how the course and certificate are structured.",
        path: "/linear-algebra/overview",
        meta: "Start here",
        icon: "★",
        logo: (
          <span className="math-logo">
            ℝ<sup>n</sup> Toolkit
          </span>
        ),
        start: true,
      },
      {
        title: "Linear Equations",
        description:
          "General form in 2, 3, and n variables, graphing with intercepts, systems, and solving techniques.",
        path: "/linear-algebra/linear-equations/1",
        meta: "2 parts · MCQ practice",
        icon: "=",
        logo: <span className="math-logo">a₁x₁ + ... + aₙxₙ = b</span>,
      },
      {
        title: "Vectors & Vector Spaces",
        description: "Vectors, span, basis, and linear independence.",
        path: "/linear-algebra/vectors/1",
        meta: "2 parts · MCQ practice",
        icon: "v",
        logo: <span className="math-logo">span&#123;v₁, ..., vₖ&#125;</span>,
      },
      {
        title: "Matrices & Determinants",
        description: "Matrix operations, inverses, and determinants.",
        path: "/linear-algebra/matrices/1",
        meta: "2 parts · MCQ practice",
        icon: "M",
        logo: <span className="math-logo">det(A) ≠ 0</span>,
      },
      {
        title: "Systems of Linear Equations",
        description: "Row reduction, rank, and consistency of linear systems.",
        path: "/linear-algebra/systems/1",
        meta: "2 parts · MCQ practice",
        icon: "Σ",
        logo: <span className="math-logo">[A | b] → [I | x]</span>,
      },
      {
        title: "Eigenvalues & Eigenvectors",
        description:
          "Characteristic polynomials, eigenspaces, algebraic vs geometric multiplicity, diagonalization, matrix powers, and the Spectral Theorem.",
        path: "/linear-algebra/eigen/1",
        meta: "2 parts · MCQ practice",
        icon: "λ",
        logo: <span className="math-logo">Av = λv</span>,
      },
      {
        title: "Linear Transformations",
        description:
          "Standard matrices, kernel & image, composition, and invertibility.",
        path: "/linear-algebra/transformations/1",
        meta: "2 parts · MCQ practice",
        icon: "T",
        logo: <span className="math-logo">T(u + v) = Tu + Tv</span>,
      },
      {
        title: "Orthogonality & Least Squares",
        description:
          "Inner products, norms, orthogonal matrices, Gram–Schmidt, QR, projections, and least-squares solutions.",
        path: "/linear-algebra/orthogonality/1",
        meta: "2 parts · MCQ practice",
        icon: "⊥",
        logo: (
          <span className="math-logo">
            Q<sup>T</sup>Q = I
          </span>
        ),
      },
      {
        title: "Singular Value Decomposition",
        description:
          "Full and compact SVD, singular values, fundamental subspaces, conditioning, low-rank approximation, and the pseudoinverse.",
        path: "/linear-algebra/svd/1",
        meta: "2 parts · MCQ practice",
        icon: "Σ",
        logo: (
          <span className="math-logo">
            A = U Σ V<sup>T</sup>
          </span>
        ),
      },
      ...LA_MODULES.map((module) => ({
        title: module.title,
        description: module.description,
        path: getLaModulePath(module),
        meta: "2 parts · 4 topics · 80 checkpoint MCQs",
        icon: module.logo,
        logo: <span className="math-logo">{module.logo}</span>,
      })),
      ...LA_EXPANSION_MODULES.map((module) => ({
        title: module.title,
        description: module.description,
        path: getLaModulePath(module),
        icon: module.logo,
        logo: <span className="math-logo">{module.logo}</span>,
        meta: `${getLaModuleParts(module).length} available part${getLaModuleParts(module).length === 1 ? "" : "s"} · ${module.topics.length} topic${module.topics.length === 1 ? "" : "s"} · ${module.topics.length * 20} checkpoint MCQs`,
      })),
      {
        title: "Practice Arena",
        description:
          "MCQ drills for Linear Algebra topics with leaderboard submit.",
        path: "/practice",
        meta: "Interactive · Difficulty tiers",
        icon: "✎",
        logo: <span className="math-logo">✎ Drill Arena</span>,
      },
      {
        title: "Function Grapher",
        description:
          "Plot functions, trace values, and see derivatives, tangents, roots and turning points.",
        path: "/grapher",
        meta: "Interactive · tool",
        icon: "📈",
        logo: <span className="math-logo">y = f(x)</span>,
      },
      {
        title: "Timed Mock Exam",
        description:
          "30 mixed practice MCQs (10 Easy · 10 Medium · 10 Hard) in 45 minutes, with a topic-by-topic breakdown.",
        path: "/mock-exam/linear-algebra",
        meta: "Interactive · 30 questions · 45 min",
        icon: "⏱",
        logo: <span className="math-logo">⏱ 45:00</span>,
      },
      {
        title: "Certification Quiz",
        description:
          "99 MCQs covering the full course. Score 80%+ to unlock your certificate.",
        path: "/quiz/linear-algebra",
        meta: "99 questions · 80% to pass",
        icon: "✓",
        logo: <span className="math-logo">✓ 66 MCQs · 80%</span>,
      },
    ],
  },
  {
    id: "probability-statistics",
    title: "Probability and Statistics",
    logo: (
      <span className="math-logo-card">
        <span className="math-sym-big">P</span>(A|B)
      </span>
    ),
    description:
      "Probability foundations, Bayes theorem, random variables, distributions, descriptive statistics, hypothesis testing, and regression analysis.",
    path: "/courses/probability-statistics",
    meta: "6 modules · Guides + tools",
    icon: "P",
    color: "purple",
    heroImage: "/images/courses/prob-stats-hero.svg",
    heroImageAlt:
      "Probability and Statistics Gaussian Bell Curve and Regression Visual",
    overview: {
      longDescription: [
        "Probability and Statistics bridges mathematical theory and real-world empirical inference. Master sample spaces, probability axioms, conditional probability, and Bayes' Theorem. Explore discrete and continuous random variables, probability mass and density functions, expected values, variance, and fundamental distributions (Binomial, Poisson, Normal, Exponential). Learn descriptive statistics, central limit theorem, hypothesis testing (z-tests, t-tests, p-values), and linear regression with residual analysis.",
      ],
      highlights: [
        "Sample spaces, axioms & conditional probability",
        "Bayes' Theorem & interactive Bayes screening lab",
        "Discrete & continuous random variables (PMF, PDF, CDF)",
        "Mean, variance, z-scores & central limit theorem",
        "Hypothesis testing, significance levels & p-values",
        "Linear regression, correlation coefficient & residuals",
        "Certificate quiz — 30 MCQs, 80% to pass",
      ],
      prerequisites: "Basic algebra and single-variable calculus.",
    },
    modules: [
      {
        title: "Overview",
        description:
          "What you'll learn, the order to learn it in, and how the course and certificate are structured.",
        path: "/probability-statistics/overview",
        meta: "Start here",
        icon: "★",
        logo: <span className="math-logo">P(A|B) &amp; N(μ,σ²) Roadmap</span>,
        start: true,
      },
      {
        title: "Probability Basics",
        description:
          "Sample spaces, axioms, conditional probability, and Bayes' Theorem.",
        path: "/probability-statistics/probability-basics/1",
        meta: "2 parts · Worked examples",
        icon: "P",
        logo: (
          <span className="math-logo">
            P(A|B) = <sup>P(A∩B)</sup>/<sub>P(B)</sub>
          </span>
        ),
      },
      {
        title: "Bayes Lab",
        description:
          "Interactive Bayesian screening and posterior probability calculator.",
        path: "/probability-statistics/bayes-lab",
        meta: "Interactive tool",
        icon: "⚖",
        logo: (
          <span className="math-logo">
            P(B|A) = <sup>P(A|B)P(B)</sup>/<sub>P(A)</sub>
          </span>
        ),
      },
      {
        title: "Distribution Explorer",
        description:
          "Explore Normal, Binomial, Poisson, Exponential, t and chi-square distributions with live sliders.",
        path: "/distribution-explorer",
        meta: "Interactive tool",
        icon: "📈",
        logo: <span className="math-logo">f(x) = P(X = x)</span>,
      },
      {
        title: "Statistics Calculator",
        description:
          "Descriptive statistics, t-tests and regression from your own data, with the working shown.",
        path: "/stats-calculator",
        meta: "Interactive tool",
        icon: "🧮",
        logo: <span className="math-logo">x̄ ± t*·s/√n</span>,
      },
      {
        title: "Badges & Achievements",
        description:
          "See which badges you have unlocked and how close you are to the next one.",
        path: "/achievements",
        meta: "Your progress",
        icon: "🏅",
        logo: <span className="math-logo">🏅 5 badges</span>,
      },
      {
        title: "Random Variables & Distributions",
        description:
          "Discrete and continuous random variables, PMFs, PDFs, expectation, and variance.",
        path: "/probability-statistics/random-variables/1",
        meta: "2 parts · Worked examples",
        icon: "X",
        logo: <span className="math-logo">E[X] = μ, Var(X) = σ²</span>,
      },
      {
        title: "Descriptive Statistics",
        description:
          "Mean, median, mode, variance, standard deviation, and data visualization.",
        path: "/probability-statistics/descriptive-statistics/1",
        meta: "2 parts · Worked examples",
        icon: "σ",
        logo: (
          <span className="math-logo">
            x̄, s² = <sup>1</sup>/<sub>n-1</sub>Σ(x-x̄)²
          </span>
        ),
      },
      {
        title: "Hypothesis Testing",
        description:
          "Null and alternative hypotheses, z-tests, t-tests, and p-value inference.",
        path: "/probability-statistics/hypothesis-testing/1",
        meta: "2 parts · Worked examples",
        icon: "H",
        logo: (
          <span className="math-logo">
            z = <sup>(x̄ - μ₀)</sup>/<sub>(σ/√n)</sub>
          </span>
        ),
      },
      {
        title: "Regression & Correlation",
        description:
          "Linear association, least squares regression, and residual analysis.",
        path: "/probability-statistics/regression-correlation/1",
        meta: "2 parts · MCQ practice",
        icon: "ρ",
        logo: <span className="math-logo">ŷ = β₀ + β₁x</span>,
      },
      ...PS_EXPANSION_MODULES.map((module) => ({
        title: module.title,
        description: module.description,
        path: getPsModulePath(module),
        meta: `${module.topics.length} topics · ${module.topics.length * 20} checkpoint MCQs`,
        icon: module.logo,
        logo: <span className="math-logo">{module.logo}</span>,
      })),
      {
        title: "Practice Arena",
        description:
          "MCQ drills for Probability & Statistics with leaderboard submit.",
        path: "/practice",
        meta: "Interactive · Difficulty tiers",
        icon: "✎",
        logo: <span className="math-logo">✎ Drill Arena</span>,
      },
      {
        title: "Function Grapher",
        description:
          "Plot functions, trace values, and see derivatives, tangents, roots and turning points.",
        path: "/grapher",
        meta: "Interactive · tool",
        icon: "📈",
        logo: <span className="math-logo">y = f(x)</span>,
      },
      {
        title: "Timed Mock Exam",
        description:
          "30 mixed practice MCQs (10 Easy · 10 Medium · 10 Hard) in 45 minutes, with a topic-by-topic breakdown.",
        path: "/mock-exam/probability-statistics",
        meta: "Interactive · 30 questions · 45 min",
        icon: "⏱",
        logo: <span className="math-logo">⏱ 45:00</span>,
      },
      {
        title: "Certification Quiz",
        description:
          "30 MCQs covering the full course. Score 80%+ to unlock your certificate.",
        path: "/quiz/probability-statistics",
        meta: "30 questions · 80% to pass",
        icon: "✓",
        logo: <span className="math-logo">✓ 30 MCQs · 80%</span>,
      },
    ],
  },
];

export function getCourseById(id) {
  return COURSES.find((c) => c.id === id) || null;
}
